/** Session key: one automatic Google re-auth after PreSignUp link (survives OAuth redirect remount). */
export const GOOGLE_ACCOUNT_LINK_RETRY_KEY =
  "inprofile_google_account_link_retry";

/** User started Google OAuth from login or signup (Hub may not know flow after redirect). */
export const GOOGLE_OAUTH_PENDING_KEY = "inprofile_google_oauth_pending";

export function clearGoogleAccountLinkRetryAttempt() {
  try {
    sessionStorage.removeItem(GOOGLE_ACCOUNT_LINK_RETRY_KEY);
  } catch (_) {
    /* ignore */
  }
}

export function hasGoogleAccountLinkRetryAttempted() {
  try {
    return sessionStorage.getItem(GOOGLE_ACCOUNT_LINK_RETRY_KEY) === "1";
  } catch (_) {
    return false;
  }
}

export function markGoogleAccountLinkRetryAttempted() {
  try {
    sessionStorage.setItem(GOOGLE_ACCOUNT_LINK_RETRY_KEY, "1");
  } catch (_) {
    /* ignore */
  }
}

export function setGoogleOAuthPending() {
  try {
    sessionStorage.setItem(GOOGLE_OAUTH_PENDING_KEY, "1");
  } catch (_) {
    /* ignore */
  }
}

export function clearGoogleOAuthPending() {
  try {
    sessionStorage.removeItem(GOOGLE_OAUTH_PENDING_KEY);
  } catch (_) {
    /* ignore */
  }
}

export function isGoogleOAuthPending() {
  try {
    return sessionStorage.getItem(GOOGLE_OAUTH_PENDING_KEY) === "1";
  } catch (_) {
    return false;
  }
}

function collectDeepStrings(value, out, depth) {
  if (depth > 12) return;
  if (value == null) return;
  if (typeof value === "string") {
    out.push(value);
    return;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    out.push(String(value));
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) collectDeepStrings(item, out, depth + 1);
    return;
  }
  if (typeof value === "object") {
    for (const k of Object.keys(value))
      collectDeepStrings(value[k], out, depth + 1);
  }
}

/**
 * Cognito PreSignUp throws `AccountLinked` to block a duplicate `google_*` user after AdminLinkProviderForUser.
 * Client should retry federated sign-in once so the second PreSignUp path returns event (already linked).
 */
export function isGoogleAccountLinkFirstPassFailure(data) {
  const out = [];
  collectDeepStrings(data, out, 0);
  let s = out.join(" ");
  if (s.length < 8 && data && typeof data === "object") {
    try {
      s = JSON.stringify(data);
    } catch (_) {
      s = "";
    }
  }
  const lower = s.toLowerCase();
  return (
    s.includes("AccountLinked") ||
    s.includes("PreSignUp failed") ||
    s.includes("UserLambdaValidationException") ||
    lower.includes("presignup failed") ||
    lower.includes("invalidlambdaresponseexception") ||
    lower.includes("unexpectedlambdaresponseexception") ||
    lower.includes("lambda: invalidresult") ||
    lower.includes("error returned from presignup")
  );
}

export function decodeOAuthErrorDescription(raw) {
  if (raw == null || raw === "") return "";
  try {
    return decodeURIComponent(String(raw).replace(/\+/g, " "));
  } catch (_) {
    return String(raw);
  }
}

/** Flatten Cognito / Amplify / URL error payloads into one readable string (handles `PreSignUp+failed+...`). */
export function flattenAuthErrorText(errorLike) {
  if (errorLike == null) return "";
  if (typeof errorLike === "string") {
    return decodeOAuthErrorDescription(String(errorLike).replace(/\+/g, " "));
  }
  if (errorLike instanceof Error) {
    return decodeOAuthErrorDescription(
      String(errorLike.message || "").replace(/\+/g, " "),
    );
  }

  const parts = [];
  if (typeof errorLike?.message === "string") parts.push(errorLike.message);
  if (typeof errorLike?.error_description === "string")
    parts.push(errorLike.error_description);
  if (typeof errorLike?.error === "string") parts.push(errorLike.error);
  if (typeof errorLike?.code === "string") parts.push(errorLike.code);

  const out = [];
  collectDeepStrings(errorLike, out, 0);
  let s = [...parts, ...out].join(" ").trim();
  if (!s) {
    try {
      s = JSON.stringify(errorLike);
    } catch (_) {
      s = "";
    }
  }
  return decodeOAuthErrorDescription(s.replace(/\+/g, " "));
}

export function isAccountLinkedOAuthError(errorLike) {
  return flattenAuthErrorText(errorLike)
    .toLowerCase()
    .includes("accountlinked");
}

/**
 * Cognito token endpoint 400 `invalid_grant` (e.g. reused auth `code`, expired code, PKCE mismatch).
 * In this app we surface a product message aimed at the common case: email/password account exists
 * for the same email as Google, or a second hop after linking must start a fresh OAuth round.
 */
export function isOAuthInvalidGrantError(errorLike) {
  return flattenAuthErrorText(errorLike)
    .toLowerCase()
    .includes("invalid_grant");
}

const OAUTH_INVALID_GRANT_SESSION_KEY = "inprofile_oauth_invalid_grant";

/** Hub may emit `invalid_grant` after `resolveGoogleLogin` already read the user — persist for one consume. */
export function markOAuthInvalidGrantRejected() {
  try {
    sessionStorage.setItem(OAUTH_INVALID_GRANT_SESSION_KEY, "1");
  } catch (_) {
    /* ignore */
  }
}

export function consumeOAuthInvalidGrantRejected() {
  try {
    const v = sessionStorage.getItem(OAUTH_INVALID_GRANT_SESSION_KEY);
    sessionStorage.removeItem(OAUTH_INVALID_GRANT_SESSION_KEY);
    return v === "1";
  } catch (_) {
    return false;
  }
}

export function clearOAuthInvalidGrantSessionFlag() {
  try {
    sessionStorage.removeItem(OAUTH_INVALID_GRANT_SESSION_KEY);
  } catch (_) {
    /* ignore */
  }
}

const OAUTH_CALLBACK_PARAM_KEYS = new Set([
  "code",
  "state",
  "error",
  "error_description",
  "error_uri",
  "scope",
  "session_state",
  "nonce",
  "access_token",
  "id_token",
  "token_type",
]);

/**
 * Remove Cognito / OAuth redirect parameters from the current URL so the SDK
 * does not try to exchange the same authorization `code` twice (common `invalid_grant`
 * after an AccountLinked PreSignUp hop + immediate Google retry).
 */
export function stripOAuthCallbackParamsFromUrl() {
  if (typeof window === "undefined") return;
  try {
    const u = new URL(window.location.href);
    for (const k of [...u.searchParams.keys()]) {
      if (OAUTH_CALLBACK_PARAM_KEYS.has(k.toLowerCase()))
        u.searchParams.delete(k);
    }
    const qs = u.searchParams.toString();
    u.search = qs ? `?${qs}` : "";

    if (u.hash && u.hash.length > 1) {
      const raw = u.hash.slice(1);
      if (raw.includes("=") && !raw.startsWith("/")) {
        const hp = new URLSearchParams(raw);
        for (const k of [...hp.keys()]) {
          if (OAUTH_CALLBACK_PARAM_KEYS.has(k.toLowerCase())) hp.delete(k);
        }
        const hq = hp.toString();
        u.hash = hq ? `#${hq}` : "";
      }
    }

    const next = `${u.pathname}${u.search}${u.hash}`;
    const prev = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (next !== prev) {
      window.history.replaceState({}, document.title, next);
    }
  } catch (_) {
    /* ignore */
  }
}

let lastAccountLinkedUiToastAt = 0;

/** Avoid duplicate Ant Design toasts when URL parsing and Hub both see the same AccountLinked hop. */
export function shouldShowAccountLinkedUiToast() {
  const now = Date.now();
  if (now - lastAccountLinkedUiToastAt < 4000) return false;
  lastAccountLinkedUiToastAt = now;
  return true;
}

/**
 * User-visible copy for hosted UI / Hub failures (not raw Cognito strings).
 */
export function getFederatedSignInFailureUserMessage(errorLike) {
  const text = flattenAuthErrorText(errorLike);
  const lower = text.toLowerCase();

  if (lower.includes("accountlinked")) {
    return "Your Google account was linked to your existing profile. We’re finishing sign-in—this usually takes one more step.";
  }

  if (
    lower.includes("presignup failed") ||
    lower.includes("userlambdavalidationexception") ||
    lower.includes("error returned from presignup")
  ) {
    return "Google sign-in could not be completed. Please try again, or sign in with your email and password.";
  }

  if (lower.includes("googleaccountexists")) {
    return "An account with this email already exists. Please sign in with Google, or use email and password if you created the account that way.";
  }

  if (lower.includes("invalid_grant")) {
    return "This email is already registered with email and password. Please sign in below using your email and password.";
  }

  return text || "Google sign-in did not complete. Please try again.";
}

/**
 * After Cognito reports Google / hosted UI success, loading the app account can still fail
 * (e.g. GraphQL errors, missing username mapping). Prefer a clear message over a blank Amplify error.
 */
export function getGooglePostSignInSetupErrorMessage(errorLike) {
  const t = flattenAuthErrorText(errorLike);
  if (t) {
    if (isOAuthInvalidGrantError(errorLike)) {
      return getFederatedSignInFailureUserMessage(errorLike);
    }
    return t;
  }
  return "Google sign-in worked, but we could not load your Inprofile account. If you originally signed up with email and password, use those fields below. Otherwise try Google again in a moment.";
}

/** Reads OAuth error text from query and hash (some flows put errors in the fragment). */
export function getCombinedOAuthErrorStringFromWindow() {
  if (typeof window === "undefined") return "";
  const parts = [];
  const searchParams = new URLSearchParams(window.location.search || "");
  parts.push(searchParams.get("error_description"), searchParams.get("error"));
  const hash = window.location.hash || "";
  if (hash.length > 1) {
    const h = hash.startsWith("#") ? hash.slice(1) : hash;
    const hashParams = new URLSearchParams(h);
    parts.push(hashParams.get("error_description"), hashParams.get("error"));
  }
  return parts
    .filter(Boolean)
    .map((p) => decodeOAuthErrorDescription(p))
    .join(" ");
}

export const getSignupErrorMessage = (error) => {
  const code = String(error?.code || "");
  const message = String(error?.message || "");

  if (message.includes("GoogleAccountExists")) {
    return "An account with this email already exists via Google. Please sign in with Google.";
  }

  if (code === "UsernameExistsException") {
    return "An account with this email already exists. Please sign in.";
  }

  if (
    message.includes("GoogleAccountExists") ||
    message.includes("UserLambdaValidationException") ||
    message.includes("PreSignUp failed")
  ) {
    return "An account with this email already exists via Google. Please sign in with Google.";
  }

  return message || "Signup failed. Please try again.";
};

export const getForgotPasswordErrorMessage = (error) => {
  const code = String(error?.code || "");
  const message = String(error?.message || "");

  if (
    code === "InvalidParameterException" ||
    code === "NotAuthorizedException"
  ) {
    return "This account uses Google Sign-In. Please sign in with Google instead.";
  }

  if (code === "UserNotFoundException") {
    return "No password-based account was found for that email. If you created this account with Google, use “Sign in with Google” instead.";
  }

  return message || "Something went wrong.";
};

export const getLoginErrorMessage = (error) => {
  const code = String(error?.code || "");
  const flat = flattenAuthErrorText(error);
  const message = flat || String(error?.message || "");
  const lower = message.toLowerCase();

  if (lower.includes("accountlinked")) {
    return "Your Google account was linked to your existing profile. If sign-in did not continue automatically, click “Sign in with Google” once more.";
  }

  if (
    code === "UserLambdaValidationException" ||
    message.includes("PreSignUp failed") ||
    lower.includes("error returned from presignup")
  ) {
    return "Google sign-in could not be completed. Please try again, or sign in with email and password.";
  }

  if (lower.includes("invalid_grant")) {
    return getFederatedSignInFailureUserMessage({ message: "invalid_grant" });
  }

  if (code === "NotAuthorizedException") {
    return 'Incorrect password. If you signed up with Google, please use "Sign in with Google".';
  }

  if (code === "UserNotFoundException") {
    return "No account found with this email or it may be registered via Google.";
  }

  return message || "Login failed. Please try again.";
};
